import { useTenderDeadline } from "../../../../hooks/useTenderDeadline";
import { useCallback, useEffect, useRef, useState } from "react";
import { Aviation } from "../../../../services";

export const useAviationTenderState = ({ order, orderId, isOpen, isLoading, userCompanyId }) => {
  const { isExpired: isTenderExpiredByServer, isReady: isTenderTimeReady } = useTenderDeadline(order?.tenderDateEnd);
  const requestIdRef = useRef(0);
  const [bidsState, setBidsState] = useState(null);
  const isCurrentOrder = isOpen && order?.id != null && String(order.id) === String(orderId);
  const isCurrentBidsState = bidsState?.order === order && bidsState?.companyId === userCompanyId;
  const bidsLoaded = isCurrentOrder && isCurrentBidsState && bidsState.status === "loaded";
  const bidsError = isCurrentBidsState && bidsState?.status === "error";
  const previousBids = isCurrentBidsState ? bidsState.bids : [];
  const status = String(order?.status || "").toLowerCase();
  const isDirectAssignment = order?.assignmentMethod === "DirectAssignment";
  const keepOrderDetails =
    status === "awaitingoffer" ||
    (userCompanyId != null && order?.expeditorId != null && String(order.expeditorId) === String(userCompanyId));
  const isTenderEnded = Boolean(order?.tenderDateEnd) && isTenderExpiredByServer;
  const isExpiredAwaitingTender = !isDirectAssignment && status === "awaitingoffer" && isTenderEnded;
  const isOtherExpeditorAssigned =
    userCompanyId != null &&
    order?.expeditorId != null &&
    order.expeditorId !== "" &&
    String(order.expeditorId) !== String(userCompanyId);
  const showBidBlock =
    !isDirectAssignment && bidsLoaded && previousBids.length > 0 &&
    (status === "canceled" || String(order.expeditorId) !== String(userCompanyId));
  const showTenderClosedInfo =
    !isDirectAssignment && bidsLoaded && previousBids.length === 0 && status !== "canceled" &&
    ((status === "awaitingoffer" && isTenderEnded) || (status !== "awaitingoffer" && isOtherExpeditorAssigned));

  const loadPreviousBids = useCallback(async () => {
    if (!isCurrentOrder || userCompanyId == null) return;
    const requestId = ++requestIdRef.current;
    const nextState = { order, companyId: userCompanyId, bids: [] };
    setBidsState({ ...nextState, status: "loading" });
    try {
      const response = await Aviation.getSuggestedPriceByExpeditor(order.id);
      if (requestId !== requestIdRef.current) return;
      if (!Array.isArray(response?.data)) throw new Error("Invalid aviation bids response");
      setBidsState({ ...nextState, bids: response.data, status: "loaded" });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setBidsState({ ...nextState, status: "error" });
    }
  }, [isCurrentOrder, order, userCompanyId]);

  useEffect(() => {
    setBidsState(null);
    loadPreviousBids();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadPreviousBids]);

  let view = "hidden";
  if (isCurrentOrder) {
    if (isLoading || userCompanyId == null) view = "loading";
    else if (keepOrderDetails && (!isExpiredAwaitingTender || (bidsLoaded && !showTenderClosedInfo))) {
      view = "details";
    }
    else if (bidsError) view = "error";
    else if (!bidsLoaded) view = "loading";
    else if (showBidBlock || showTenderClosedInfo) view = "tender";
    else view = "details";
  }

  return {
    isTimeReady: isTenderTimeReady,
    view,
    previousBids,
    bidsLoaded,
    loadPreviousBids,
    isTenderEnded,
    isOtherExpeditorAssigned,
    showBidBlock,
    showTenderClosedInfo,
    showBidsError: isCurrentOrder && !isLoading && bidsError,
  };
};
